import { defineStore, storeToRefs } from 'pinia';
import { ref, watchEffect, watch } from 'vue';
import { useMultihostService } from '@commentsold/multihost';
import { refDebounced } from '@vueuse/core';
import { useAppStore } from './useAppStore';
import { useNetworkQualityStore } from './useNetworkQualityStore';
import { useBroadcastSettingsStore } from './useBroadcastSettingsStore';
export const Errors = {
  Reconnecting: {
    header: 'Reconnecting',
    body: 'Lost connection to Live Stream. Attempting to reconnect. Reload the page if reconnection does not occur in 10 seconds.',
    dialogType: 'normal',
    location: 'top',
  },
  UnstableNetwork: {
    header: 'Connection Unstable',
    body: 'Your network connection is having issues. Reducing stream quality may help.',
    location: 'top',
    dialogType: 'network',
  },
  PoorNetwork: {
    header: 'Connection Poor',
    body: 'Your network connection is having issues. Reducing stream quality may help.',
    location: 'top',
    dialogType: 'network',
  },
  PermissionNeeded: {
    header: 'Permission Needed',
    body: 'In order to use Videeo, head over to your site settings section and enable your microphone and camera. Refresh the browser when finished.',
    location: 'top',
    dialogType: 'normal',
  },
  MicrophoneNotFound: {
    header: 'Microphone not detected',
    body: 'Looks like something went wrong with your microphone. Try refreshing your browser. If the problem persists reach out to support',
    location: 'top',
    dialogType: 'normal',
  },
  CameraNotFound: {
    header: 'Camera not detected',
    body: 'Looks like something went wrong with your camera. Try flipping camera views on your device. If the problem persists reach out to suppport',
    location: 'top',
    dialogType: 'normal',
  },
  WaitingForDirector: {
    header: 'Waiting for Director',
    body: 'Waiting for a user to select the multihost option in the Go Live interface',
    location: 'top',
    dialogType: 'normal',
  },
  AgoraOrNetworkError: {
    header: 'Lost connection to live streaming servers',
    body: 'Please refresh your browser and try again. If the problem continues please check your network connection.',
    location: 'center',
    dialogType: 'normal',
  },
  HostAlreadyPresent: {
    header: 'Channel host present',
    body: 'You cannot join this channel as a different host is already present.',
    location: 'center',
    dialogType: 'normal',
  },
  DefaultError: {
    header: 'Oops!',
    body: 'Looks like something went wrong, please refresh your browser and try again. If the problem continues please reach out out to customer support.',
    location: 'center',
    dialogType: 'normal',
  },
} as const;
type ErrorItem = (typeof Errors)[keyof typeof Errors];
export const useErrorStore = defineStore('mcErrorStore', () => {
  const multihostService = useMultihostService();

  const {
    userGrantDenied,
    isUnsupportedCamera,
    microphones,
    cameras,
    didGetDevices,
    hasJoinedChannel,
    localUser,
    connectionState,
  } = storeToRefs(multihostService);

  watch(connectionState, () => {
    if (
      connectionState.value.hasBeenKickedOrBanned &&
      localUser.value.type !== 'director'
    ) {
      window.location.reload();
    }
  });

  const { suppressNetworkQualityWarnings } = storeToRefs(useAppStore());
  const { localNetworkQuality } = storeToRefs(useNetworkQualityStore());

  const broadcastSettingsStore = useBroadcastSettingsStore();
  const { showNetworkWarnings } = storeToRefs(broadcastSettingsStore);

  const debouncedNetworkQuality = refDebounced(localNetworkQuality, 100);
  const currentError = ref<ErrorItem>(Errors.DefaultError);
  const isShowingError = ref(false);
  const noop = () => {
    /**/
  };
  const errorAction = ref({
    text: '',
    action: noop,
  });
  const showError = (
    error: (typeof Errors)[keyof typeof Errors],
    action?: { text: string; action: () => void }
  ) => {
    currentError.value = error;
    isShowingError.value = true;
    errorAction.value = action || { text: '', action: noop };
  };
  const hideError = () => {
    isShowingError.value = false;
  };
  const isEqual = (errorA: ErrorItem, errorB: ErrorItem) =>
    errorA.header === errorB.header && errorA.body === errorB.body;
  const isCurrentError = (error: ErrorItem) =>
    isEqual(currentError.value, error);

  watchEffect(async () => {
    //set up reactive errors
    if (
      !isEqual(currentError.value, Errors.DefaultError) &&
      !isEqual(currentError.value, Errors.HostAlreadyPresent)
    ) {
      //ensure we don't hide an api error, etc if a reactive error is resolved
      hideError();
    }
    let error: (typeof Errors)[keyof typeof Errors] | null = null;
    let action: Parameters<typeof showError>[1];
    if (
      isUnsupportedCamera.value ||
      (didGetDevices.value && cameras.value.length === 0)
    ) {
      error = Errors.CameraNotFound;
    }
    if (didGetDevices.value && microphones.value.length === 0) {
      error = Errors.MicrophoneNotFound;
    }
    if (userGrantDenied.value) {
      error = Errors.PermissionNeeded;
    }
    if (
      hasJoinedChannel.value &&
      debouncedNetworkQuality.value !== 'GOOD' &&
      showNetworkWarnings.value &&
      !suppressNetworkQualityWarnings.value
    ) {
      error =
        debouncedNetworkQuality.value === 'POOR'
          ? Errors.PoorNetwork
          : Errors.UnstableNetwork;
    }
    if (connectionState.value.rtcConnectionState.state === 'RECONNECTING') {
      error = Errors.Reconnecting;
      action = {
        text: 'Reload Page',
        action() {
          window.location.reload();
        },
      };
    }
    if (error) {
      showError(error, action);
    }
  });

  return {
    currentError,
    isShowingError,
    errorAction,
    showError,
    hideError,
    isEqual,
    isCurrentError,
  };
});
